// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MySLAPage from './pages/MySLAPage';
import IncidentsPage from './pages/IncidentsPage';
import ServiceRequestsPage from './pages/ServiceRequestsPage';
import ChangeRequestsPage from './pages/ChangeRequestsPage';
import NewRequestPage from './pages/NewRequestPage';
import SLAPoliciesPage from './pages/SLAPoliciesPage';
import BreachReportPage from './pages/BreachReportPage';
import ApprovalWorkflowsPage from './pages/ApprovalWorkflowsPage';
import TeamsPage from './pages/TeamsPage';
import EscalationPage from './pages/EscalationPage';
import { ShiftCalendarPage } from './pages/ShiftCalendarPage';
import { AppOwnersPage } from './pages/AppOwnersPage';
import { AuthenticationPage } from './pages/AuthenticationPage';
import { UserRolesPage } from './pages/UserRolesPage';
import { SystemConfigPage } from './pages/SystemConfigPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import TicketDetailPage from './pages/TicketDetailPage';
import CMDBPage from './pages/CMDBPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyApprovalsPage from './pages/MyApprovalsPage';
import CategoriesPage from './pages/CategoriesPage';
import MyTicketsSearchPage from './pages/MyTicketsSearchPage';
import ProfilePage from './pages/ProfilePage';
import MyTicketsPage from './pages/MyTicketsPage';
import DelegationsPage from './pages/DelegationsPage';
import MyBucketPage from './pages/MyBucketPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import HelpPage from './pages/HelpPage';
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/my-sla" element={<MySLAPage />} />
        <Route path="/incidents" element={<IncidentsPage />} />
        <Route path="/service-requests" element={<ServiceRequestsPage />} />
        <Route path="/change-requests" element={<ChangeRequestsPage />} />
        <Route path="/new-request" element={<NewRequestPage />} />
        <Route path="/sla-policies" element={<SLAPoliciesPage />} />
        <Route path="/breach-report" element={<BreachReportPage />} />
        <Route path="/approval-workflows" element={<ApprovalWorkflowsPage />} />
        <Route path="/shift-calendar" element={<ShiftCalendarPage />} />
        <Route path="/teams" element={<TeamsPage />} />
        <Route path="/escalation" element={<EscalationPage />} />
        <Route path="/app-owners" element={<AppOwnersPage />} />
        <Route path="/authentication" element={<AuthenticationPage />} />
        <Route path="/user-roles" element={<UserRolesPage />} />
        <Route path="/system-config" element={<SystemConfigPage />} />
        <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="/ticket/:type/:id" element={<TicketDetailPage />} />
        <Route path="/cmdb" element={<CMDBPage />} />
        <Route path="/my-approvals" element={<MyApprovalsPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/my-tickets-search" element={<MyTicketsSearchPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/my-tickets" element={<MyTicketsPage />} />
        <Route path="/my-bucket" element={<MyBucketPage />} />
        <Route path="/delegations" element={<DelegationsPage />} />
        <Route path="/audit-logs" element={<AuditLogsPage />} />
        <Route path="/help" element={<HelpPage />} />
        {/* Superadmin — standalone page, no AppLayout */}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Superadmin — completely independent, never blocked by auth loading */}
            <Route path="/superadmin" element={<SuperAdminPage />} />
            {/* All other routes go through normal auth flow */}
            <Route path="*" element={<AppRoutes />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
